import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Wand2, Hash, Clock, TrendingUp, UserCircle, Loader2, Copy, Lock, Zap, Bookmark, MessageCircle } from "lucide-react";

const ALL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];

const copy = (t) => { navigator.clipboard.writeText(t).then(() => toast.success("Copied!")).catch(() => {}); };

const PlatformSelect = ({ value, onChange, platforms }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 bg-transparent border-white/10 w-full sm:w-44" data-testid="studio-platform-select"><SelectValue /></SelectTrigger>
    <SelectContent>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
  </Select>
);

const Panel = ({ children }) => <div className="rounded-xl border border-border/50 bg-card p-6 space-y-4">{children}</div>;

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-2">{items?.map((h, i) => <Badge key={i} className="bg-primary/10 text-primary border border-primary/20">{h.startsWith("#") ? h : `#${h.replace(/^#/, "")}`}</Badge>)}</div>
);

const AIStudio = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const platforms = (user?.platforms?.length ? user.platforms.map((p) => ({ id: p.platform, name: p.platform })) : ALL_PLATFORMS)
    .map((p) => ({ id: p.id, name: ALL_PLATFORMS.find((x) => x.id === p.id)?.name || p.id }));
  const defaultPf = platforms[0]?.id || "tiktok";
  const locked = user && user.role !== "admin" && user.subscription_status !== "active";

  // per-tool state
  const [pf, setPf] = useState(defaultPf);
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("engaging");
  const [about, setAbout] = useState("");
  const [comment, setComment] = useState("");
  const [replyTone, setReplyTone] = useState("friendly");
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState({});

  const run = async (key, fn) => {
    setBusy(key);
    try { const data = await fn(); setRes((r) => ({ ...r, [key]: data })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setBusy(""); }
  };

  const saveToLibrary = async (type, title, content) => {
    try { await api.post("/library", { type, platform: pf, title, content }); toast.success("Saved to Library"); }
    catch { toast.error("Could not save"); }
  };

  if (locked) {
    return (
      <DashboardShell>
        <div className="p-6 md:p-10 max-w-2xl mx-auto">
          <div className="rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-12 text-center" data-testid="studio-locked">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-6"><Lock className="w-8 h-8 text-primary" /></div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Unlock the AI Studio</h1>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">Generate viral scripts, captions, hashtags, posting schedules, weekly trends and an optimized bio — all tailored to your niche.</p>
            <Button onClick={() => navigate("/plans")} className="mt-7 rounded-full h-12 px-8 bg-primary text-background font-bold" data-testid="studio-upgrade-btn">
              Choose a plan <Zap className="w-4 h-4 ml-1" fill="currentColor" />
            </Button>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Sparkles className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">AI Studio</h1>
        </div>
        <p className="text-muted-foreground mb-8">Real, ToS-safe growth: AI creates the content, you post it and the algorithm rewards you.</p>

        <Tabs defaultValue="script">
          <TabsList className="bg-card border border-border/50 flex-wrap h-auto">
            <TabsTrigger value="script" data-testid="tab-script"><Wand2 className="w-4 h-4 mr-1" /> Script</TabsTrigger>
            <TabsTrigger value="caption" data-testid="tab-caption"><Hash className="w-4 h-4 mr-1" /> Caption</TabsTrigger>
            <TabsTrigger value="besttime" data-testid="tab-besttime"><Clock className="w-4 h-4 mr-1" /> Best Time</TabsTrigger>
            <TabsTrigger value="trends" data-testid="tab-trends"><TrendingUp className="w-4 h-4 mr-1" /> Trends</TabsTrigger>
            <TabsTrigger value="replies" data-testid="tab-replies"><MessageCircle className="w-4 h-4 mr-1" /> Replies</TabsTrigger>
            <TabsTrigger value="bio" data-testid="tab-bio"><UserCircle className="w-4 h-4 mr-1" /> Bio</TabsTrigger>
          </TabsList>

          {/* SCRIPT */}
          <TabsContent value="script" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Video topic, e.g. 'morning skincare routine'"
                  className="h-11 bg-transparent border-white/10 flex-1" data-testid="script-topic-input" />
                <Button onClick={() => topic.trim() ? run("script", async () => (await api.post("/ai/script", { platform: pf, topic })).data.result) : toast.error("Enter a topic")}
                  disabled={busy === "script"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="script-generate-btn">
                  {busy === "script" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
                </Button>
              </div>
              {res.script && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-2" data-testid="script-result">
                  <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Title</div><div className="font-heading text-xl font-bold">{res.script.title}</div></div>
                  <div className="rounded-lg bg-primary/5 border border-primary/20 p-4"><div className="text-xs uppercase tracking-widest text-primary mb-1">Hook</div><div className="font-semibold">{res.script.hook}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Script beats</div>
                    <ol className="space-y-2">{res.script.beats?.map((b, i) => <li key={i} className="flex gap-3 text-sm"><span className="text-primary font-mono">{i + 1}.</span> {b}</li>)}</ol></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">CTA</div><div className="text-sm">{res.script.cta}</div></div>
                    <div><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Caption</div><div className="text-sm">{res.script.caption}</div></div>
                  </div>
                  <Chips items={res.script.hashtags} />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copy(`${res.script.title}\n\nHook: ${res.script.hook}\n\n${(res.script.beats || []).join("\n")}\n\nCTA: ${res.script.cta}\n\n${res.script.caption}\n${(res.script.hashtags || []).map((h) => "#" + h.replace(/^#/, "")).join(" ")}`)} className="rounded-full border-white/15" data-testid="script-copy-btn"><Copy className="w-3.5 h-3.5 mr-1" /> Copy all</Button>
                    <Button variant="outline" size="sm" onClick={() => saveToLibrary("script", res.script.title || topic, res.script)} className="rounded-full border-white/15" data-testid="script-save-btn"><Bookmark className="w-3.5 h-3.5 mr-1" /> Save</Button>
                  </div>
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* CAPTION */}
          <TabsContent value="caption" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="h-11 bg-transparent border-white/10 w-full sm:w-40" data-testid="caption-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["engaging", "funny", "inspirational", "bold", "educational"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What's the post about?" className="h-11 bg-transparent border-white/10 flex-1" data-testid="caption-topic-input" />
                <Button onClick={() => topic.trim() ? run("caption", async () => (await api.post("/ai/caption", { platform: pf, topic, tone })).data.result) : toast.error("Enter a topic")}
                  disabled={busy === "caption"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="caption-generate-btn">{busy === "caption" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}</Button>
              </div>
              {res.caption && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-2" data-testid="caption-result">
                  {res.caption.captions?.map((c, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4 flex items-start justify-between gap-3">
                      <div className="text-sm">{c}</div>
                      <Button variant="ghost" size="sm" onClick={() => copy(c)} className="shrink-0"><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Chips items={res.caption.hashtags} />
                  <Button variant="outline" size="sm" onClick={() => saveToLibrary("caption", topic || "Captions", res.caption)} className="rounded-full border-white/15" data-testid="caption-save-btn"><Bookmark className="w-3.5 h-3.5 mr-1" /> Save to Library</Button>
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* BEST TIME */}
          <TabsContent value="besttime" className="mt-6">
            <Panel>
              <div className="flex gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Button onClick={() => run("besttime", async () => (await api.post("/ai/besttime", { platform: pf, topic: "-" })).data.result)}
                  disabled={busy === "besttime"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="besttime-generate-btn">{busy === "besttime" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Get schedule"}</Button>
              </div>
              {res.besttime && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 pt-2" data-testid="besttime-result">
                  <div className="flex flex-wrap gap-2">{res.besttime.best_times?.map((t, i) => <Badge key={i} className="bg-primary/10 text-primary border border-primary/20 text-sm py-1"><Clock className="w-3 h-3 mr-1" />{t}</Badge>)}</div>
                  <div className="rounded-lg bg-white/5 border border-white/10 p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Cadence</div><div className="text-sm">{res.besttime.cadence}</div></div>
                  {res.besttime.note && <p className="text-sm text-muted-foreground">💡 {res.besttime.note}</p>}
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* TRENDS */}
          <TabsContent value="trends" className="mt-6">
            <Panel>
              <div className="flex gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Button onClick={() => run("trends", async () => (await api.get(`/ai/trends?platform=${pf}`)).data.result)}
                  disabled={busy === "trends"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="trends-generate-btn">{busy === "trends" ? <Loader2 className="w-4 h-4 animate-spin" /> : "This week's trends"}</Button>
              </div>
              {res.trends && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 gap-3 pt-2" data-testid="trends-result">
                  {res.trends.trends?.map((t, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4">
                      <div className="font-heading font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" />{t.title}</div>
                      <div className="text-sm text-muted-foreground mt-1">{t.angle}</div>
                      <div className="text-xs text-primary mt-2">Why now: {t.why_now}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* REPLIES */}
          <TabsContent value="replies" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Select value={replyTone} onValueChange={setReplyTone}>
                  <SelectTrigger className="h-11 bg-transparent border-white/10 w-full sm:w-40" data-testid="replies-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["friendly", "funny", "grateful", "witty", "supportive"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Paste a follower's comment, e.g. 'This changed how I do my skincare!'" className="bg-transparent border-white/10 min-h-[80px]" data-testid="replies-comment-input" />
              <Button onClick={() => comment.trim() ? run("replies", async () => (await api.post("/ai/replies", { platform: pf, comment, tone: replyTone })).data.result) : toast.error("Paste a comment to reply to")}
                disabled={busy === "replies"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="replies-generate-btn">{busy === "replies" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Draft replies"}</Button>
              {res.replies && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-2" data-testid="replies-result">
                  {res.replies.replies?.map((c, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4 flex items-start justify-between gap-3">
                      <div className="text-sm">{c}</div>
                      <Button variant="ghost" size="sm" onClick={() => copy(c)} className="shrink-0" data-testid={`replies-copy-${i}`}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => saveToLibrary("replies", comment.slice(0, 40) || "Replies", res.replies)} className="rounded-full border-white/15" data-testid="replies-save-btn"><Bookmark className="w-3.5 h-3.5 mr-1" /> Save to Library</Button>
                </motion.div>
              )}
            </Panel>
          </TabsContent>

          {/* BIO */}
          <TabsContent value="bio" className="mt-6">
            <Panel>
              <div className="flex flex-col sm:flex-row gap-3">
                <PlatformSelect value={pf} onChange={setPf} platforms={platforms} />
                <Textarea value={about} onChange={(e) => setAbout(e.target.value)} placeholder="Describe yourself / your content in a sentence or two" className="bg-transparent border-white/10 flex-1 min-h-[44px]" data-testid="bio-about-input" />
              </div>
              <Button onClick={() => about.trim() ? run("bio", async () => (await api.post("/ai/bio", { platform: pf, about })).data.result) : toast.error("Tell us about you")}
                disabled={busy === "bio"} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="bio-generate-btn">{busy === "bio" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Optimize bio"}</Button>
              {res.bio && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-2" data-testid="bio-result">
                  {res.bio.bios?.map((b, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-4 flex items-start justify-between gap-3"><div className="text-sm font-medium">{b}</div><Button variant="ghost" size="sm" onClick={() => copy(b)} className="shrink-0"><Copy className="w-3.5 h-3.5" /></Button></div>
                  ))}
                  <ul className="space-y-1.5 pt-2">{res.bio.tips?.map((t, i) => <li key={i} className="text-sm text-muted-foreground flex gap-2">✓ {t}</li>)}</ul>
                </motion.div>
              )}
            </Panel>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardShell>
  );
};

export default AIStudio;
